/**
 * PartnerAttributionFooter — Phase 7.
 *
 * Renders the partner branding block on public invitation pages.
 * Conditionally shows phone, website, location, and logo based on the
 * attribution settings returned by the backend. All display decisions
 * are driven by backend data — the frontend never overrides them.
 *
 * Safety rule: this component never renders internal partner data
 * (ownerName, legalName, status, onboarding). Only PublicAttribution fields.
 */

import React from 'react';
import type { PublicAttribution } from '@/src/modules/partner/types';
import { safeImageUrl, safeLinkUrl } from '@/src/modules/renderer/url-safety';

interface Props {
  attribution: PublicAttribution;
  themeBgColor?: string | null;
}

function isDarkColor(colorHex: string | null | undefined): boolean {
  if (!colorHex) return false;
  const hex = colorHex.replace('#', '');
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq < 128;
  }
  if (hex.length === 6) {
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq < 128;
  }
  return false;
}

export function PartnerAttributionFooter({ attribution, themeBgColor }: Props) {
  if (!attribution.enabled) return null;

  // Defence in depth: the backend validates these as URLs on write, but the
  // public page must never navigate to / load a `javascript:` or other unsafe
  // scheme even if a bad value slips through.
  const logoUrl = attribution.logoUrl ? safeImageUrl(attribution.logoUrl) : null;
  const websiteUrl = attribution.websiteUrl ? safeLinkUrl(attribution.websiteUrl) : null;
  const googleMapsUrl = attribution.googleMapsUrl ? safeLinkUrl(attribution.googleMapsUrl) : null;

  const isDark = themeBgColor ? isDarkColor(themeBgColor) : false;

  return (
    <section data-section-type="partner-attribution" className="mt-12 mb-8 px-4 text-center">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-400 mb-4">
        Created by
      </p>

      <div className="flex flex-col items-center gap-2">
        {attribution.displayLogo && logoUrl && (
          <img
            src={logoUrl}
            alt={attribution.businessName}
            className="h-12 w-auto object-contain rounded"
          />
        )}

        <p className={`text-base font-semibold ${isDark ? 'text-white' : 'text-neutral-800'}`}>
          {attribution.businessName}
        </p>

        {attribution.customMessage && (
          <p className={`text-sm italic max-w-xs ${isDark ? 'text-neutral-300' : 'text-neutral-500'}`}>
            {attribution.customMessage}
          </p>
        )}

        <div className={`flex flex-wrap justify-center gap-x-4 gap-y-1 mt-1 text-sm ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
          {attribution.displayPhone && attribution.phone && (
            <a
              href={`tel:${attribution.phone}`}
              className={isDark ? 'hover:text-white transition-colors' : 'hover:text-neutral-900 transition-colors'}
              aria-label="Call partner"
            >
              📞 {attribution.phone}
            </a>
          )}

          {attribution.displayWebsite && websiteUrl && (
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={isDark ? 'hover:text-white transition-colors' : 'hover:text-neutral-900 transition-colors'}
              aria-label="Visit partner website"
            >
              🌐 {websiteLabel(websiteUrl)}
            </a>
          )}

          {attribution.displayLocation && googleMapsUrl && (
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={isDark ? 'hover:text-white transition-colors' : 'hover:text-neutral-900 transition-colors'}
              aria-label="View location on map"
            >
              📍 View location
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

function websiteLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
